import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Modal, Select, Tooltip, message } from 'antd';
import { Plus } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  REQ_REASON, STAGE, STAGES, branchById, daysBetween, empById, fmtD, fmtM, positionById, reqCandidates, reqHired, toDay,
  type ReqDraft, type ReqStatus, type Requisition, type Stage,
} from './hrLogic';
import { Badge, Chip, PageHeader, ReqStatusTag, reqUrl, useHrPerms } from './hrUi';
import { RequisitionModal } from './hrModals';

type F = ReqStatus | 'all';

/** Duyệt / từ chối yêu cầu tuyển dụng (dùng chung cho danh sách và trang chi tiết) */
export function useReqActions() {
  const { me } = useHrPerms();
  const s = useHr();
  const approve = (r: Requisition) => Modal.confirm({
    title: `Duyệt yêu cầu ${r.code}?`,
    content: `${r.qty} ${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}${r.overPlan ? ' — VƯỢT ĐỊNH BIÊN, cần Ban giám đốc đồng ý' : ''}`,
    okText: 'Duyệt', cancelText: 'Đóng',
    onOk: () => { const x = hrApi.approveRequisition(r.id, me); if (x.ok) message.success('Đã duyệt'); else message.error(x.errors?.join(', ')); },
  });
  const reject = (r: Requisition) => {
    let reason = '';
    Modal.confirm({
      title: `Từ chối yêu cầu ${r.code}`, okText: 'Từ chối', okButtonProps: { danger: true }, cancelText: 'Đóng',
      content: <Input.TextArea rows={3} placeholder="Lý do (bắt buộc)" onChange={e => { reason = e.target.value; }} />,
      onOk: () => {
        const x = hrApi.rejectRequisition(r.id, me, reason);
        if (!x.ok) { message.error(x.errors?.join(', ')); return Promise.reject(); }
        message.success('Đã từ chối');
        return undefined;
      },
    });
  };
  const close = (r: Requisition, cancel = false) => {
    let note = '';
    Modal.confirm({
      title: `${cancel ? 'Hủy' : 'Đóng'} yêu cầu ${r.code}?`, okText: cancel ? 'Hủy yêu cầu' : 'Đóng yêu cầu', cancelText: 'Thôi',
      content: <div className="space-y-2"><div className="text-xs text-gray-500">Ứng viên còn trong quy trình sẽ được đóng hồ sơ.</div><Input placeholder="Ghi chú" onChange={e => { note = e.target.value; }} /></div>,
      onOk: () => { hrApi.closeRequisition(r.id, me, note, cancel); message.success('Đã cập nhật'); },
    });
  };
  return { approve, reject, close };
}

/** Yêu cầu tuyển dụng: lý do, định biên, tiến độ tuyển, hạn */
export default function Requisitions({ openNew = false }: { openNew?: boolean }) {
  const s = useHr();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canRecruit, canApprove } = useHrPerms();
  const { approve, reject } = useReqActions();
  const today = toDay();
  const f = (params.get('f') as F) || 'approved';
  const branchId = params.get('branch') || '';
  const [init, setInit] = useState<Partial<ReqDraft> | null>(() => {
    if (!openNew && !params.get('new')) return null;
    const e = empById(s, params.get('replace') || undefined);
    if (!e) return {};
    const pos = positionById(s, e.positionId);
    return { branchId: e.branchId, positionId: e.positionId, reason: 'replace', replaceEmpIds: [e.id], qty: 1, salaryMin: pos?.salaryMin, salaryMax: pos?.salaryMax };
  });
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const scoped = s.requisitions.filter(r => !branchId || r.branchId === branchId);
  const list = scoped.filter(r => f === 'all' || r.status === f).sort((a, b) => (f === 'approved' ? a.deadline.localeCompare(b.deadline) : b.createdAt - a.createdAt));
  const FILTERS: [F, string][] = [['approved', 'Đang tuyển'], ['pending', 'Chờ duyệt'], ['closed', 'Đã đóng'], ['rejected', 'Từ chối'], ['cancelled', 'Đã hủy'], ['all', 'Tất cả']];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Yêu cầu tuyển dụng" desc="Bộ phận đề xuất → duyệt (vượt định biên cần Ban giám đốc) → tuyển → tự đóng khi đủ người"
          extra={canRecruit && <Button type="primary" icon={<Plus size={14} />} onClick={() => setInit({})}>Tạo yêu cầu</Button>} />
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => <Chip key={k} active={f === k} tone={k === 'pending' && scoped.some(r => r.status === 'pending') ? 'amber' : 'gray'} onClick={() => go({ f: k === 'approved' ? null : k })}>{l} · {k === 'all' ? scoped.length : scoped.filter(r => r.status === k).length}</Chip>)}
          <Select className="w-[180px] ml-auto" value={branchId} onChange={v => go({ branch: v || null })} options={[{ value: '', label: 'Tất cả chi nhánh' }, ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {list.length === 0 && <Empty className="py-10 lg:col-span-2" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có yêu cầu" />}
          {list.map(r => {
            const pos = positionById(s, r.positionId);
            const hired = reqHired(s, r);
            const cands = reqCandidates(s, r.id);
            const open = cands.filter(c => c.outcome === 'open');
            const left = daysBetween(today, r.deadline);
            return (
              <div key={r.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 flex flex-col gap-3 cursor-pointer hover:border-[#3e91ab]" onClick={() => navigate(reqUrl(r.id))}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{pos?.name} <span className="text-gray-400 font-normal">× {r.qty}</span></div>
                    <div className="text-xs text-gray-500">{r.code} · {branchById(s, r.branchId)?.name} · {REQ_REASON[r.reason]}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1"><ReqStatusTag status={r.status} />{r.overPlan && <Badge className="bg-violet-50 text-violet-700 border-violet-200">Vượt định biên</Badge>}</div>
                </div>
                {r.replaceEmpIds?.length ? <div className="text-xs text-gray-500">Thay cho: {r.replaceEmpIds.map(id => empById(s, id)?.name).filter(Boolean).join(', ')}</div> : null}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(hired / r.qty) * 100}%` }} /></div>
                  <span className="text-xs text-gray-600">Đã tuyển {hired}/{r.qty}</span>
                </div>
                {r.status === 'approved' && (
                  <div className="flex gap-1">
                    {STAGES.slice(0, 5).map((st: Stage) => {
                      const n = open.filter(c => c.stage === st).length;
                      return (
                        <Tooltip key={st} title={STAGE[st].label}>
                          <div className="flex-1 rounded-md text-center py-1" style={{ background: n ? `${STAGE[st].color}22` : '#f3f4f6' }}>
                            <div className="text-sm font-semibold" style={{ color: n ? STAGE[st].color : '#9ca3af' }}>{n}</div>
                            <div className="text-[10px] text-gray-500">{STAGE[st].short}</div>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <span>{fmtM(r.salaryMin)} – {fmtM(r.salaryMax)} · {cands.length} hồ sơ</span>
                  {r.status === 'approved' || r.status === 'pending'
                    ? <span className={left < 0 ? 'text-rose-600 font-medium' : left <= 7 ? 'text-amber-600' : ''}>Hạn {fmtD(r.deadline)} {left < 0 ? `(trễ ${-left} ngày)` : `(còn ${left} ngày)`}</span>
                    : <span>{r.status === 'rejected' ? r.rejectReason : r.closeNote || ''}</span>}
                </div>
                {r.status === 'pending' && canApprove && (
                  <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                    <Button size="small" type="primary" onClick={() => approve(r)}>Duyệt</Button>
                    <Button size="small" danger onClick={() => reject(r)}>Từ chối</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {init && <RequisitionModal init={init} onClose={() => { setInit(null); if (params.get('new')) go({ new: null, replace: null }); }} onSaved={id => navigate(reqUrl(id))} />}
    </Layout>
  );
}

